import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  RefreshCw, Check, ArrowRight, ArrowLeft, ChevronRight, Search, 
  Tag, Package, Layers, CheckCircle2, HelpCircle, Settings, 
  ShoppingBag, Store, Link2
} from "lucide-react";

const API = "/api";

// Wizard steps
const STEPS = [
  { id: "category", title: "Kategori Eşleştirme", icon: Layers },
  { id: "attribute", title: "Özellik Eşleştirme", icon: Tag },
  { id: "brand", title: "Marka Eşleştirme", icon: Store },
  { id: "summary", title: "Tamamlandı (Özet)", icon: CheckCircle2 }
];

export default function TrendyolSetupPage() {
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Shopify data
  const [shopifyCategories, setShopifyCategories] = useState([]);
  const [shopifyProductTypes, setShopifyProductTypes] = useState([]);
  const [shopifyBrands, setShopifyBrands] = useState([]);
  
  // Trendyol data
  const [trendyolCategories, setTrendyolCategories] = useState([]);
  const [trendyolBrands, setTrendyolBrands] = useState([]);
  const [categoryAttributes, setCategoryAttributes] = useState({});
  
  // Selected items for hierarchical navigation
  const [selectedLevel1, setSelectedLevel1] = useState(null);
  const [selectedLevel2, setSelectedLevel2] = useState(null);
  const [selectedLevel3, setSelectedLevel3] = useState(null);
  
  // Mappings
  const [categoryMappings, setCategoryMappings] = useState([]);
  const [brandMappings, setBrandMappings] = useState([]);
  
  // Search
  const [shopifySearch, setShopifySearch] = useState("");
  const [trendyolCategorySearch, setTrendyolCategorySearch] = useState("");
  const [brandSearch, setBrandSearch] = useState("");

  useEffect(() => {
    fetchInitialData();
  }, [token]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [
        shopifyCategoriesRes,
        shopifyTypesRes,
        trendyolCategoriesRes,
        categoryMappingsRes,
        brandMappingsRes
      ] = await Promise.all([
        axios.get(`${API}/shopify/categories?token=${token}`),
        axios.get(`${API}/shopify/product-types?token=${token}`),
        axios.get(`${API}/trendyol/categories?token=${token}`),
        axios.get(`${API}/trendyol/category-mappings?token=${token}`),
        axios.get(`${API}/trendyol/brand-mappings?token=${token}`)
      ]);
      
      setShopifyCategories(shopifyCategoriesRes.data.categories || []);
      setShopifyProductTypes(shopifyTypesRes.data.product_types || []);
      setTrendyolCategories(trendyolCategoriesRes.data.categories || []);
      setCategoryMappings(categoryMappingsRes.data.mappings || []);
      setBrandMappings(brandMappingsRes.data.mappings || []);
      
      // Extract unique brands from Shopify products
      const brandsSet = new Set();
      shopifyTypesRes.data.product_types?.forEach(pt => {
        if (pt) brandsSet.add(pt);
      });
      setShopifyBrands(Array.from(brandsSet));
      
    } catch (error) {
      console.error("Data fetch error:", error);
      toast.error("Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendyolBrands = async (searchName = "") => {
    try {
      const res = await axios.get(`${API}/trendyol/brands?token=${token}&name=${searchName}&size=100`);
      setTrendyolBrands(res.data.brands || []);
    } catch (error) {
      console.error("Brand fetch error:", error);
    }
  };

  const fetchCategoryAttributes = async (categoryId) => {
    try {
      const res = await axios.get(`${API}/trendyol/category-attributes/${categoryId}?token=${token}`);
      setCategoryAttributes(prev => ({
        ...prev,
        [categoryId]: res.data.attributes || {}
      }));
    } catch (error) {
      console.error("Attribute fetch error:", error);
    }
  };

  // Get categories by parent ID for hierarchical display
  const getCategoriesByParent = (parentId = null) => {
    if (!parentId) {
      return trendyolCategories.filter(c => !c.parentId);
    }
    return trendyolCategories.filter(c => c.parentId === parentId);
  };

  // Filter categories by search
  const filterCategories = (categories, search) => {
    if (!search) return categories;
    const lower = search.toLowerCase();
    return categories.filter(c => c.name?.toLowerCase().includes(lower));
  };

  // Save category mapping
  const saveCategoryMapping = async (shopifyCategory, trendyolCategory) => {
    try {
      await axios.post(`${API}/trendyol/category-mappings?token=${token}`, {
        shopify_category: shopifyCategory,
        trendyol_category_id: trendyolCategory.id,
        trendyol_category_name: trendyolCategory.name
      });
      
      setCategoryMappings(prev => [
        ...prev.filter(m => m.shopify_category !== shopifyCategory),
        {
          shopify_category: shopifyCategory,
          trendyol_category_id: trendyolCategory.id,
          trendyol_category_name: trendyolCategory.name
        }
      ]);
      
      toast.success("Kategori eşleştirmesi kaydedildi");
    } catch (error) {
      toast.error("Kayıt hatası");
    }
  };

  // Save brand mapping
  const saveBrandMapping = async (shopifyBrand, trendyolBrand) => {
    try {
      await axios.post(`${API}/trendyol/brand-mappings?token=${token}`, {
        shopify_brand: shopifyBrand,
        trendyol_brand_id: trendyolBrand.id,
        trendyol_brand_name: trendyolBrand.name
      });
      
      setBrandMappings(prev => [
        ...prev.filter(m => m.shopify_brand !== shopifyBrand),
        {
          shopify_brand: shopifyBrand,
          trendyol_brand_id: trendyolBrand.id,
          trendyol_brand_name: trendyolBrand.name
        }
      ]);
      
      toast.success("Marka eşleştirmesi kaydedildi");
    } catch (error) {
      toast.error("Kayıt hatası");
    }
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Level 1 categories (root)
  const level1Categories = filterCategories(getCategoriesByParent(null), trendyolCategorySearch);
  // Level 2 categories
  const level2Categories = selectedLevel1 ? getCategoriesByParent(selectedLevel1.id) : [];
  // Level 3 categories
  const level3Categories = selectedLevel2 ? getCategoriesByParent(selectedLevel2.id) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="trendyol-setup-page">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-xl border shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-xs">trendyol</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Kurulum Sihirbazı</h1>
            <p className="text-slate-500">Shopify kategorilerini Trendyol ile eşleştirin</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <HelpCircle className="w-4 h-4 mr-2" />
            Yardım
          </Button>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-6">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            
            return (
              <div key={step.id} className="flex items-center">
                <div 
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                    isActive ? "bg-orange-50 text-orange-600" : 
                    isCompleted ? "text-green-600" : "text-slate-400"
                  }`}
                  onClick={() => setCurrentStep(index)}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isActive ? "bg-orange-500 text-white" :
                    isCompleted ? "bg-green-500 text-white" : "bg-slate-200"
                  }`}>
                    {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className="font-medium hidden lg:block">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <ChevronRight className={`w-5 h-5 mx-2 ${isCompleted ? "text-green-500" : "text-slate-300"}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500"
            style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-0">
          {/* Step 1: Category Mapping */}
          {currentStep === 0 && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-900">Shopify Kategorisi</h2>
                <p className="text-slate-500">Eşleştirmek istediğiniz Shopify kategorisini seçiniz.</p>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {/* Shopify Categories - Ana Kategori */}
                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b">
                    <h3 className="font-semibold text-slate-700">Ana Kategori</h3>
                  </div>
                  <ScrollArea className="h-[400px]">
                    <div className="p-2">
                      {shopifyProductTypes.length > 0 ? (
                        shopifyProductTypes.map((type, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${
                              selectedLevel1?.name === type ? "bg-orange-50 border border-orange-200" : ""
                            }`}
                            onClick={() => {
                              setSelectedLevel1({ id: idx, name: type });
                              setSelectedLevel2(null);
                              setSelectedLevel3(null);
                            }}
                          >
                            <span className="text-sm">{type}</span>
                            <Badge variant="outline" className="text-xs">
                              {categoryMappings.filter(m => m.shopify_category === type).length > 0 ? "Eşleşti" : ""}
                            </Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-slate-400 py-8">Kategori bulunamadı</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Trendyol Categories - Alt Kategori 1 */}
                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b">
                    <h3 className="font-semibold text-slate-700">Trendyol Kategori</h3>
                    <div className="mt-2 relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input 
                        placeholder="Kategori ara..." 
                        className="pl-9 h-9"
                        value={trendyolCategorySearch}
                        onChange={(e) => setTrendyolCategorySearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <ScrollArea className="h-[360px]">
                    <div className="p-2">
                      {level1Categories.map((cat) => (
                        <div
                          key={cat.id}
                          className={`flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${
                            selectedLevel2?.id === cat.id ? "bg-orange-50 border border-orange-200" : ""
                          }`}
                          onClick={() => {
                            setSelectedLevel2(cat);
                            setSelectedLevel3(null);
                          }}
                        >
                          <span className="text-sm">{cat.name}</span>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>

                {/* Trendyol Sub-Categories - Alt Kategori 2 */}
                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b">
                    <h3 className="font-semibold text-slate-700">Alt Kategori</h3>
                  </div>
                  <ScrollArea className="h-[360px]">
                    <div className="p-2">
                      {level2Categories.length > 0 ? (
                        level2Categories.map((cat) => (
                          <div
                            key={cat.id}
                            className={`flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${
                              selectedLevel3?.id === cat.id ? "bg-green-50 border border-green-200" : ""
                            }`}
                            onClick={() => {
                              setSelectedLevel3(cat);
                              if (selectedLevel1) {
                                saveCategoryMapping(selectedLevel1.name, cat);
                              }
                            }}
                          >
                            <span className="text-sm">{cat.name}</span>
                            <Badge variant="outline" className="text-xs">({cat.id})</Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-slate-400 py-8">
                          {selectedLevel2 ? "Alt kategori yok" : "Önce kategori seçin"}
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end mt-6">
                <Button onClick={nextStep} className="bg-orange-500 hover:bg-orange-600">
                  DEVAM ET
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Attribute Mapping */}
          {currentStep === 1 && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-900">Özellik Eşleştirme</h2>
                <p className="text-slate-500">Ürün özelliklerini Trendyol özellikleri ile eşleştirin.</p>
              </div>

              <div className="border rounded-xl p-6 bg-slate-50 text-center">
                <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">Kategori eşleştirmeleri tamamlandıktan sonra özellikler otomatik olarak eşleştirilecektir.</p>
                <Badge variant="outline">{categoryMappings.length} kategori eşleştirildi</Badge>
              </div>

              {/* Mapped Categories */}
              {categoryMappings.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold mb-3">Eşleştirilen Kategoriler</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Shopify Kategorisi</TableHead>
                          <TableHead>Trendyol Kategorisi</TableHead>
                          <TableHead>ID</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {categoryMappings.map((mapping, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{mapping.shopify_category}</TableCell>
                            <TableCell>{mapping.trendyol_category_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{mapping.trendyol_category_id}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={prevStep}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  GERİ
                </Button>
                <Button onClick={nextStep} className="bg-orange-500 hover:bg-orange-600">
                  DEVAM ET
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Brand Mapping */}
          {currentStep === 2 && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-900">Marka Eşleştirme</h2>
                <p className="text-slate-500">Shopify markalarınızı Trendyol markaları ile eşleştirin.</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Shopify Brands */}
                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b">
                    <h3 className="font-semibold text-slate-700">Shopify Markaları</h3>
                  </div>
                  <ScrollArea className="h-[400px]">
                    <div className="p-2">
                      {shopifyBrands.length > 0 ? (
                        shopifyBrands.map((brand, idx) => {
                          const mapping = brandMappings.find(m => m.shopify_brand === brand);
                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-slate-50"
                            >
                              <span className="text-sm">{brand}</span>
                              {mapping ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <Check className="w-3 h-3 mr-1" />
                                  {mapping.trendyol_brand_name}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-orange-600">
                                  Eşleşmedi
                                </Badge>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-center text-slate-400 py-8">Marka bulunamadı</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Trendyol Brands */}
                <div className="border rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b">
                    <h3 className="font-semibold text-slate-700">Trendyol Markaları</h3>
                    <div className="mt-2 flex gap-2">
                      <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input 
                          placeholder="Marka ara..." 
                          className="pl-9 h-9"
                          value={brandSearch}
                          onChange={(e) => setBrandSearch(e.target.value)}
                        />
                      </div>
                      <Button size="sm" onClick={() => fetchTrendyolBrands(brandSearch)}>
                        <Search className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <ScrollArea className="h-[360px]">
                    <div className="p-2">
                      {trendyolBrands.length > 0 ? (
                        trendyolBrands.map((brand) => (
                          <div
                            key={brand.id}
                            className="flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              // Find first unmapped Shopify brand
                              const unmapped = shopifyBrands.find(
                                sb => !brandMappings.find(m => m.shopify_brand === sb)
                              );
                              if (unmapped) {
                                saveBrandMapping(unmapped, brand);
                              }
                            }}
                          >
                            <span className="text-sm">{brand.name}</span>
                            <Badge variant="outline" className="text-xs">{brand.id}</Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-slate-400 py-8">
                          Marka aramak için yukarıdaki alana yazın
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={prevStep}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  GERİ
                </Button>
                <Button onClick={nextStep} className="bg-orange-500 hover:bg-orange-600">
                  DEVAM ET
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Summary */}
          {currentStep === 3 && (
            <div className="p-6">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Kurulum Tamamlandı!</h2>
                <p className="text-slate-500 mt-2">Trendyol entegrasyonunuz kullanıma hazır.</p>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {/* Category Summary */}
                <Card className="bg-slate-50">
                  <CardContent className="pt-6 text-center">
                    <Layers className="w-10 h-10 text-orange-500 mx-auto mb-3" />
                    <p className="text-3xl font-bold text-slate-900">{categoryMappings.length}</p>
                    <p className="text-sm text-slate-500">Kategori Eşleştirmesi</p>
                  </CardContent>
                </Card>

                {/* Attribute Summary */}
                <Card className="bg-slate-50">
                  <CardContent className="pt-6 text-center">
                    <Tag className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                    <p className="text-3xl font-bold text-slate-900">0</p>
                    <p className="text-sm text-slate-500">Özellik Eşleştirmesi</p>
                  </CardContent>
                </Card>

                {/* Brand Summary */}
                <Card className="bg-slate-50">
                  <CardContent className="pt-6 text-center">
                    <Store className="w-10 h-10 text-purple-500 mx-auto mb-3" />
                    <p className="text-3xl font-bold text-slate-900">{brandMappings.length}</p>
                    <p className="text-sm text-slate-500">Marka Eşleştirmesi</p>
                  </CardContent>
                </Card>
              </div>

              {/* Next Steps */}
              <div className="mt-8 p-6 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-3">Sonraki Adımlar</h3>
                <ul className="space-y-2 text-sm text-orange-700">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Siparişleri çekmek için "Pazaryeri Siparişleri" sayfasına gidin
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Ürün aktarımı için "Pazaryerleri &gt; Trendyol" sayfasını kullanın
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Otomatik sipariş çekme için zamanlayıcıyı aktif edin
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={prevStep}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  GERİ
                </Button>
                <Button 
                  onClick={() => window.location.href = "/marketplace-orders"}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Siparişlere Git
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
